import '@ant-design/v5-patch-for-react-19';
import React from 'react';
import {Provider} from "react-redux";
import './index.css';
import App from './App';
import store from "./js/Store";
import {IntlProvider} from "react-intl";
import {BrowserRouter} from "react-router-dom";
import {createRoot} from "react-dom/client";
import { ToastContainer } from 'react-toastify';
import * as Sentry from "@sentry/react";
import {captureSupportLoginHandoff} from "./js/services/SupportLoginHandoff";

const SUPPORTED_LANGUAGES = ['en', 'de', 'fr'];
const language = navigator.language.split(/[-_]/)[0];
const resolvedLanguage = SUPPORTED_LANGUAGES.includes(language) ? language : 'en';

// Load only the needed translation file to reduce main bundle by ~70KB [SEPAHELD-FRONTEND-PERF]
const loadMessages = (lang) => {
  switch (lang) {
    case 'de': return import('./translations/de.json').then(m => m.default);
    case 'fr': return import('./translations/fr.json').then(m => m.default);
    default:   return import('./translations/en.json').then(m => m.default);
  }
};

// Handle chunk load failures gracefully after deployments [SEPAHELD-FRONTEND-1F]
// When new code is deployed, old chunk filenames are invalidated. If a user has
// the app cached, their browser requests the old chunk URL and the server
// returns index.html (which starts with '<'), causing SyntaxError.
const CHUNK_RELOAD_KEY = 'chunk_reload_attempted';

window.addEventListener('error', (event) => {
  const msg = event.message || '';
  if (
    msg.includes('Unexpected token') ||
    msg.includes('Loading chunk') ||
    msg.includes('ChunkLoadError')
  ) {
    const alreadyReloaded = sessionStorage.getItem(CHUNK_RELOAD_KEY);
    if (!alreadyReloaded) {
      sessionStorage.setItem(CHUNK_RELOAD_KEY, 'true');
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations().then((registrations) => {
          registrations.forEach((registration) => registration.unregister());
        });
      }
      window.location.reload();
      return;
    }
    sessionStorage.removeItem(CHUNK_RELOAD_KEY);
  }
});

// Clear the reload guard on successful page load
window.addEventListener('load', () => {
  sessionStorage.removeItem(CHUNK_RELOAD_KEY);
});

const container = document.getElementById('root');
const root = createRoot(container);

const isProduction = process.env.NODE_ENV === 'production';

// Must run before Sentry.init so the support-login JWT never appears in a trace.
captureSupportLoginHandoff();

Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    sendDefaultPii: false,
    integrations: [
        // Replay is lazy-loaded after first paint to reduce initial bundle [SEPAHELD-FRONTEND-PERF]
        Sentry.browserTracingIntegration(),
        Sentry.consoleLoggingIntegration({ levels: ["log", "warn", "error"] }),
    ],
    // Reduce session replay sampling to conserve quota for error replays [SEPAHELD-FRONTEND-QUOTA]
    replaysSessionSampleRate: isProduction ? 0.05 : 1.0,
    replaysOnErrorSampleRate: 1.0,
    tracesSampleRate: isProduction ? 0.2 : 1.0,
    tracePropagationTargets: ["localhost", /^https:\/*\/sepaheld\.com\/api/],
    enableLogs: true,
    release: process.env.REACT_APP_RELEASE_VERSION
});

// Defer Sentry Replay loading until after first paint to improve FCP [SEPAHELD-FRONTEND-PERF]
// The replay integration is ~50KB and not needed for initial render
if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(() => {
        Sentry.addIntegration(Sentry.replayIntegration());
    });
} else {
    setTimeout(() => {
        Sentry.addIntegration(Sentry.replayIntegration());
    }, 2000);
}

loadMessages(resolvedLanguage).then((messages) => {
  root.render(
      <Provider store={store}>
        <IntlProvider locale={resolvedLanguage} messages={messages}>
          <React.StrictMode>
            <BrowserRouter future={{v7_startTransition: true, v7_relativeSplatPath: true}}>
              <App/>
              <ToastContainer
                position="bottom-right"
                autoClose={8000}
                newestOnTop
                style={{ zIndex: 99999 }}
              />
            </BrowserRouter>
          </React.StrictMode>
        </IntlProvider>
      </Provider>
  );
});
